import { Box } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import type { FilterField } from '../../../api/models/filters.model';
import { formatDateValue } from '../../../utils/helpers';
import DatePickerComponent from '../../DatePicker/DatePickerComponent';

interface DateRangeFilterFieldProps {
	field: FilterField;
	minValue: string | null;
	maxValue: string | null;
	onMinChange: (val: string | null) => void;
	onMaxChange: (val: string | null) => void;
}

const DateRangeFilterField: React.FC<DateRangeFilterFieldProps> = ({
	field,
	minValue,
	maxValue,
	onMinChange,
	onMaxChange,
}) => {
	const minDate = field.min ? dayjs(field.min) : undefined;
	const maxDate = field.max ? dayjs(field.max) : undefined;

	// Helper to sanitize date string to 'YYYY-MM-DD' format
	const sanitizeDate = (dateStr: string | null): string | null => {
		if (!dateStr) return null;
		const d = dayjs(dateStr);
		return d.isValid() ? d.format('YYYY-MM-DD') : null;
	};

	// Wrap change handlers to ensure both min and max are always set together
	const handleMinChange = React.useCallback(
		(val: string | null) => {
			onMinChange(sanitizeDate(val));
			// If setting min value and max is empty, auto-fill max with field default
			if (val && (!maxValue || maxValue === '') && field.max) {
				const defaultMax = formatDateValue(field.max);
				if (defaultMax) {
					onMaxChange(sanitizeDate(defaultMax));
				}
			}
		},
		[onMinChange, onMaxChange, maxValue, field.max]
	);

	const handleMaxChange = React.useCallback(
		(val: string | null) => {
			onMaxChange(sanitizeDate(val));
			// If setting max value and min is empty, auto-fill min with field default
			if (val && (!minValue || minValue === '') && field.min) {
				const defaultMin = formatDateValue(field.min);
				if (defaultMin) {
					onMinChange(sanitizeDate(defaultMin));
				}
			}
		},
		[onMaxChange, onMinChange, minValue, field.min]
	);
	return (
		<>
			<Box>
				<DatePickerComponent
					label="from"
					value={minValue}
					minDate={minDate}
					maxDate={maxDate}
					onChange={handleMinChange}
					size="small"
					testId={`date-picker-${field.name}-min`}
					clearable={false}
				/>
			</Box>
			<Box>
				<DatePickerComponent
					label="to"
					value={maxValue}
					minDate={minDate}
					maxDate={maxDate}
					onChange={handleMaxChange}
					size="small"
					testId={`date-picker-${field.name}-max`}
					clearable={false}
				/>
			</Box>
		</>
	);
};

export default DateRangeFilterField;
